import { useBetraegeSichtbar, verdeckt } from "../../../app/betraege";
import { useState } from "react";
import { outcomeAboveOriginal, parseAmountToCents } from "@talentkontor/domain";
import { api } from "../../../data";
import { Dialog, useAction } from "../../../components/ui";
import { date, decimalSeparator, money as moneyFmt } from "../../../lib/format";
import { geklappt } from "./bausteine";
import { BetragErkannt, betragUnlesbar } from "../../../components/Erkannt";
import { entferneBonus, entscheideBonus, erfasseNachkauf, legeBonusAn, sendeNachricht, setzeAusgang, storniereKooperation, verschiebeMonat, type Ausgang } from "./daten";
import { ITEM_KIND_LABEL, katalogAuswahl, itemKindCommissionable, monthText, prozent as prozentFmt, type MonatInfo } from "./model";
import type { KoopDetail, PeriodFull } from "./typen";
import "./kooperationen.css";
import { inLang, t } from "../../../lib/sprache";

/**
 * Vorgänge an einer Kooperation, die über das Bearbeiten hinausgehen (Nachprüfung 0060): Ausgang eines Monats (Storno,
 * Ausfallhonorar, nicht abgenommen), Monat verschieben, Nachkauf und Verlängerung, Storno der ganzen Kooperation, Bonus nach Kennzahl.
 * Beträge immer mit parseAmountToCents im Zahlenformat der Agentur und mit Rückfrage in Klartext (Befund 2).
 */

/** Betrag aus einem Textfeld: „500,50“, „500.50“, „1.200,00“, „1'200.00“; null leer, NaN ungültig. */
export function betragAusText(v: string): number | null {
  if (!v.trim()) return null;
  try {
    return parseAmountToCents(v);
  } catch {
    return Number.NaN;
  }
}
/** Prozent aus einem Textfeld (Komma oder Punkt); null leer, NaN ungültig oder außerhalb 0 … 100 */
export function prozentAusText(v: string): number | null {
  if (!v.trim()) return null;
  const n = Number(v.trim().replace(",", "."));
  return Number.isFinite(n) && n >= 0 && n <= 100 ? n : Number.NaN;
}

const AUSGAENGE: { key: Ausgang; label: string; text: string }[] = [
  { key: "cancelled", get label() { return t("Stornieren ohne Honorar", "Cancel without fee"); }, get text() { return t("Der Monat zählt nicht mehr als Umsatz, wird nicht angefordert und nicht abgerechnet.", "The month no longer counts as revenue, is not requested and not invoiced."); } },
  { key: "cancellation_fee", get label() { return t("Stornieren mit Ausfallhonorar", "Cancel with cancellation fee"); }, get text() { return t("Der Monat bleibt mit dem Ausfallhonorar abrechenbar; Creator und Agentur teilen es wie das Honorar.", "The month stays invoiceable with the cancellation fee; creator and agency split it like the fee."); } },
  { key: "not_accepted_reduced", get label() { return t("Nicht abgenommen · Honorar mindern", "Not accepted · reduce fee"); }, get text() { return t("Die Marke nimmt den Content nicht ab; abgerechnet wird der geminderte Betrag.", "The brand does not accept the content; the reduced amount is invoiced."); } },
  { key: "not_accepted_fee", get label() { return t("Nicht abgenommen · Ausfallhonorar in Prozent", "Not accepted · cancellation fee in percent"); }, get text() { return t("Die Marke nimmt nicht ab; abgerechnet wird ein Anteil des vereinbarten Honorars.", "The brand does not accept; a share of the agreed fee is invoiced."); } },
];

/**
 * Ausgang eines Monats: Grund Pflicht; Betrag oder Prozent; Rückfrage mit Klartext, Warnung über dem vereinbarten Honorar.
 * Auf Wunsch erfährt der Creator den Ausgang per Nachricht (Chat der Kooperation).
 */
export function AusgangDialog({ titel, period, currency, onClose, creator }: {
  titel: string; period: PeriodFull; currency: string; onClose: () => void;
  creator?: { talentId: string; dealId: string; rufname: string };
}) {
  const sicht = useBetraegeSichtbar();
  const money = verdeckt(sicht, moneyFmt);
  const prozent = verdeckt(sicht, prozentFmt);
  const { busy, error, run } = useAction();
  const [nachricht, setNachricht] = useState(!!creator);
  const vereinbart = period.original_amount_cents ?? period.amount_cents ?? 0;
  const [art, setArt] = useState<Ausgang>(period.outcome && period.outcome !== "cancelled" ? period.outcome : "cancelled");
  const [grund, setGrund] = useState(period.cancel_reason ?? "");
  const [wert, setWert] = useState("");
  const [modus, setModus] = useState<"betrag" | "prozent">(art === "not_accepted_fee" ? "prozent" : "betrag");
  const [pruefen, setPruefen] = useState<{ cents: number | null; prozent: number | null } | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const prozentModus = art === "not_accepted_fee" || (art === "cancellation_fee" && modus === "prozent");
  const weiter = () => {
    if (grund.trim().length < 3) return setFehler(t("Bitte einen Grund angeben.", "Please state a reason."));
    if (art === "cancelled") {
      setFehler(null);
      return setPruefen({ cents: null, prozent: null });
    }
    if (prozentModus) {
      const p = prozentAusText(wert);
      if (p == null || Number.isNaN(p) || p === 0) return setFehler(t("Bitte einen Prozentsatz zwischen 0 und 100 eingeben, z. B. 50.", "Please enter a percentage between 0 and 100, e.g. 50."));
      setFehler(null);
      return setPruefen({ cents: Math.round((vereinbart * p) / 100), prozent: p });
    }
    const c = betragAusText(wert);
    if (c == null || Number.isNaN(c) || c <= 0) return setFehler(t(`Bitte einen Betrag wie 500${decimalSeparator()}00 eingeben.`, `Please enter an amount such as 500${decimalSeparator()}00.`));
    setFehler(null);
    return setPruefen({ cents: c, prozent: null });
  };
  // Nachricht an den Creator: deutsch, unabhängig von der Oberflächensprache
  const text = () => inLang("de", () => {
    const a = AUSGAENGE.find((x) => x.key === art)!;
    const betrag = pruefen?.cents != null ? ` Abgerechnet werden ${money(pruefen.cents, currency)} netto statt ${money(vereinbart, currency)}; bitte stelle deine Rechnung über deinen Anteil daran.` : "";
    return `${titel}: ${a.label}. Grund: ${grund.trim()}.${betrag}`;
  });
  const speichern = () => {
    if (!pruefen) return;
    void run("ausgang", async () => {
      await setzeAusgang(api, period.id, { ausgang: art, grund, betragCents: pruefen.prozent != null ? null : pruefen.cents, prozent: pruefen.prozent });
      if (nachricht && creator) await sendeNachricht(api, creator.talentId, text(), creator.dealId);
      return true as const;
    }, art === "cancelled" ? t("Monat storniert. Er zählt nicht mehr als Umsatz.", "Month cancelled. It no longer counts as revenue.") : t("Gespeichert. Der Monat bleibt mit diesem Betrag abrechenbar.", "Saved. The month stays invoiceable with this amount.")).then((r) => r && onClose());
  };
  const aktuell = AUSGAENGE.find((a) => a.key === art)!;
  return (
    <Dialog open onClose={onClose} title={t(`${titel} · Storno oder nicht abgenommen`, `${titel} · cancellation or not accepted`)}>
      {!pruefen ? (
        <>
          <fieldset className="stack">
            <legend>{t("Was ist passiert?", "What happened?")}</legend>
            {AUSGAENGE.map((a) => (
              <label key={a.key} className="row tk-koop-wahl">
                <input type="radio" name="ausgang" checked={art === a.key} onChange={() => { setArt(a.key); setWert(""); setModus(a.key === "not_accepted_fee" ? "prozent" : "betrag"); }} />
                <span>{a.label}</span>
              </label>
            ))}
          </fieldset>
          <p className="source-note">{aktuell.text} {t("Vereinbart sind ", "Agreed are ")}{money(vereinbart, currency)}{t("; der Betrag bleibt erhalten und kommt mit „Wieder öffnen“ zurück.", "; the amount is kept and comes back with “Reopen”.")}</p>
          <label>{t("Grund", "Reason")}<input value={grund} onChange={(e) => setGrund(e.target.value)} maxLength={500} placeholder={t("z. B. Kampagne von der Marke abgesagt", "e.g. campaign cancelled by the brand")} /></label>
          {art === "cancellation_fee" && (
            <label>{t("Ausfallhonorar als", "Cancellation fee as")}<select value={modus} onChange={(e) => { setModus(e.target.value as "betrag" | "prozent"); setWert(""); }}>
                <option value="betrag">{t("Betrag netto in ", "Amount net in ")}{currency}</option>
                <option value="prozent">{t("Prozent des vereinbarten Honorars", "Percent of the agreed fee")}</option>
              </select>
            </label>
          )}
          {art !== "cancelled" && (
            <label>{prozentModus ? t("Prozent des vereinbarten Honorars", "Percent of the agreed fee") : art === "not_accepted_reduced" ? t(`Geminderter Betrag netto in ${currency}`, `Reduced amount net in ${currency}`) : t(`Ausfallhonorar netto in ${currency}`, `Cancellation fee net in ${currency}`)}
              <input inputMode="decimal" value={wert} aria-invalid={(!prozentModus && betragUnlesbar(wert)) || undefined} onChange={(e) => setWert(e.target.value)} placeholder={prozentModus ? "z. B. 50" : `z. B. 500${decimalSeparator()}00`} />
              {!prozentModus && <BetragErkannt text={wert} currency={currency} />}
            </label>
          )}
          {fehler && <p className="tk-error" role="alert">{fehler}</p>}
          <div className="row">
            <button className="primary" onClick={weiter}>{t("Weiter", "Next")}</button>
            <button onClick={onClose}>{t("Abbrechen", "Cancel")}</button>
          </div>
        </>
      ) : (
        <>
          <p><strong>{t("Bitte prüfen:", "Please check:")}</strong> {aktuell.label} {t("für ", "for ")}{titel}.</p>
          {pruefen.cents != null && (
            <p>{t("Abgerechnet werden ", "To be invoiced: ")}<strong>{money(pruefen.cents, currency)}</strong> {t("netto", "net")}{pruefen.prozent != null ? t(` (${prozent(pruefen.prozent)} von ${money(vereinbart, currency)})`, ` (${prozent(pruefen.prozent)} of ${money(vereinbart, currency)})`) : ` statt ${money(vereinbart, currency)}`}.</p>
          )}
          {pruefen.cents != null && outcomeAboveOriginal(pruefen.cents, vereinbart) && (
            <p className="tk-error" role="alert">{t("Der Betrag liegt über dem vereinbarten Honorar von ", "The amount exceeds the agreed fee of ")}{money(vereinbart, currency)}{t(". Stimmt das Zahlenformat?", ". Is the number format correct?")}</p>
          )}
          <p>{t("Grund: ", "Reason: ")}{grund.trim()}</p>
          {creator && (
            <label className="row tk-koop-wahl">
              <input type="checkbox" checked={nachricht} onChange={(e) => setNachricht(e.target.checked)} />
              <span>{creator.rufname} {t("per Nachricht informieren: „", "inform by message: “")}{text()}“</span>
            </label>
          )}
          {error && <p className="tk-error" role="alert">{error}</p>}
          <div className="row">
            <button className={art === "cancelled" ? "danger" : "primary"} disabled={busy !== null} onClick={speichern}>{art === "cancelled" ? t("Monat stornieren", "Cancel month") : t("So speichern", "Save like this")}</button>
            <button onClick={() => setPruefen(null)}>{t("Zurück", "Back")}</button>
          </div>
        </>
      )}
    </Dialog>
  );
}

/** Leistungsmonat verschieben (alle Modelle): Anforderung und Content-Frist wandern mit. */
export function VerschiebenDialog({ titel, period, belegt, onClose }: { titel: string; period: PeriodFull; belegt: readonly string[]; onClose: () => void }) {
  const { busy, error, run } = useAction();
  const [monat, setMonat] = useState(period.month.slice(0, 7));
  const vergeben = monat !== period.month.slice(0, 7) && belegt.includes(monat);
  return (
    <Dialog open onClose={onClose} title={`${titel} verschieben`}>
      <p>{t("Der Monat wandert samt Anforderung, Positionen und Content-Deadline", "The month moves together with request, items and content deadline")}{period.content_deadline ? t(` (bisher ${date(period.content_deadline)})`, ` (previously ${date(period.content_deadline)})`) : ""}{t(". Mit Rechnungen steht der Leistungsmonat fest.", ". With invoices the service month is fixed.")}</p>
      <label>{t("Neuer Leistungsmonat", "New service month")}<input type="month" value={monat} onChange={(e) => setMonat(e.target.value)} /></label>
      {vergeben && <p className="tk-error" role="alert">{t("Für ", "For ")}{monthText(monat)} {t("gibt es in der Kooperation schon einen Eintrag.", "there is already an entry in the deal.")}</p>}
      {error && <p className="tk-error" role="alert">{error}</p>}
      <div className="row">
        <button className="primary" disabled={busy !== null || vergeben || !/^\d{4}-\d{2}$/.test(monat) || monat === period.month.slice(0, 7)}
          onClick={() => void run("verschieben", () => geklappt(verschiebeMonat(api, period.id, monat)), t(`Auf ${monthText(monat)} verschoben.`, `Moved to ${monthText(monat)}.`)).then((r) => r && onClose())}>
          {t("Verschieben", "Move")}</button>
        <button onClick={onClose}>{t("Abbrechen", "Cancel")}</button>
      </div>
    </Dialog>
  );
}

/**
 * Nachkauf oder Verlängerung (Nutzungsrechte, Whitelisting, Spark Ads): eigener Vorgang im Monat der Vereinbarung. Ist der
 * Monat schon abgerechnet, lässt er sich erneut anfordern (Nachberechnung); „Nutzungsrechte bis“ wird fortgeschrieben.
 */
export function NachkaufDialog({ detail, monat, onClose }: { detail: KoopDetail; monat: string; onClose: () => void }) {
  const sicht = useBetraegeSichtbar();
  const money = verdeckt(sicht, moneyFmt);
  const prozent = verdeckt(sicht, prozentFmt);
  const { busy, error, run } = useAction();
  const arten = katalogAuswahl(detail.itemKinds, Object.keys(ITEM_KIND_LABEL));
  const [art, setArt] = useState(arten.some((a) => a.key === "usage_rights") ? "usage_rights" : arten[0]?.key ?? "other");
  const [beschreibung, setBeschreibung] = useState("");
  const [betrag, setBetrag] = useState("");
  const [provision, setProvision] = useState(itemKindCommissionable(art, detail.itemKinds));
  const [wann, setWann] = useState(new Date().toISOString().slice(0, 7) >= monat ? new Date().toISOString().slice(0, 7) : monat);
  const [bis, setBis] = useState(detail.deal.usage_rights_until ?? "");
  const [pruefen, setPruefen] = useState<number | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const currency = detail.deal.currency;
  const weiter = () => {
    const c = betragAusText(betrag);
    if (beschreibung.trim().length < 2) return setFehler(t("Bitte beschreiben, was nachgekauft oder verlängert wird.", "Please describe what is purchased additionally or extended."));
    if (c == null || Number.isNaN(c) || c <= 0) return setFehler(t(`Bitte einen Betrag wie 500${decimalSeparator()}00 eingeben.`, `Please enter an amount such as 500${decimalSeparator()}00.`));
    if (!/^\d{4}-\d{2}$/.test(wann)) return setFehler(t("Bitte den Monat angeben.", "Please enter the month."));
    setFehler(null);
    setPruefen(c);
  };
  const vorhanden = detail.periods.some((p) => p.month.slice(0, 7) === wann);
  return (
    <Dialog open onClose={onClose} title={t("Nachkauf oder Verlängerung", "Follow-up purchase or extension")}>
      {pruefen == null ? (
        <>
          <p>{t("Zum Beispiel eine Verlängerung der Nutzungsrechte oder Whitelisting nach der Kampagne. Die Position wird geteilt wie das Honorar, angefordert und an die Marke abgerechnet.", "For example an extension of the usage rights or whitelisting after the campaign. The item is split like the fee, requested and invoiced to the brand.")}</p>
          <div className="grid">
            <label>{t("Art", "Type")}<select value={art} onChange={(e) => { setArt(e.target.value); setProvision(itemKindCommissionable(e.target.value, detail.itemKinds)); }}>
              {arten.map((a) => <option key={a.key} value={a.key}>{a.label}</option>)}</select></label>
            <label>{t("Beschreibung", "Description")}<input value={beschreibung} maxLength={200} onChange={(e) => setBeschreibung(e.target.value)} placeholder={t("z. B. Nutzungsrechte-Verlängerung 6 Monate", "e.g. usage rights extension 6 months")} /></label>
            <label>{t("Betrag netto in ", "Amount net in ")}{currency}<input inputMode="decimal" value={betrag} aria-invalid={betragUnlesbar(betrag) || undefined} onChange={(e) => setBetrag(e.target.value)} placeholder={`z. B. 500${decimalSeparator()}00`} /><BetragErkannt text={betrag} currency={currency} /></label>
            <label>{t("Monat der Vereinbarung", "Month of the agreement")}<input type="month" value={wann} onChange={(e) => setWann(e.target.value)} /></label>
            {art === "usage_rights" && <label>{t("Nutzungsrechte bis (optional)", "Usage rights until (optional)")}<input type="date" value={bis} onChange={(e) => setBis(e.target.value)} /></label>}
          </div>
          <label className="row tk-koop-wahl"><input type="checkbox" checked={provision} onChange={(e) => setProvision(e.target.checked)} /><span>{t("provisionspflichtig", "commissionable")}</span></label>
          <p className="source-note">{vorhanden ? t(`${monthText(wann)} gibt es in der Kooperation; die Position kommt dazu. Ist der Monat schon abgerechnet, fordert ihr eine Nachberechnung an.`, `${monthText(wann)} already exists in the deal; the item is added. If the month has already been invoiced, you request a supplementary invoice.`) : t(`Für ${monthText(wann)} entsteht ein eigener Monat „Nachkauf“ mit eigener Anforderung und Rechnung.`, `For ${monthText(wann)} a separate “Follow-up purchase” month is created with its own request and invoice.`)}</p>
          {fehler && <p className="tk-error" role="alert">{fehler}</p>}
          <div className="row"><button className="primary" onClick={weiter}>{t("Weiter", "Next")}</button><button onClick={onClose}>{t("Abbrechen", "Cancel")}</button></div>
        </>
      ) : (
        <>
          <p><strong>{t("Bitte prüfen:", "Please check:")}</strong> {beschreibung.trim()} · {money(pruefen, currency)} {t("netto · ", "net · ")}{monthText(wann)}{provision ? "" : t(" · provisionsfrei", " · commission-free")}{art === "usage_rights" && bis ? t(` · Nutzungsrechte bis ${date(bis)}`, ` · usage rights until ${date(bis)}`) : ""}.</p>
          {error && <p className="tk-error" role="alert">{error}</p>}
          <div className="row">
            <button className="primary" disabled={busy !== null} onClick={() => void run("nachkauf", () => geklappt(erfasseNachkauf(api, detail.deal.id, {
              monat: wann, art, beschreibung, betragCents: pruefen, provisionspflichtig: provision, nutzungBis: art === "usage_rights" ? bis || null : null,
            })), t("Nachkauf erfasst. Jetzt beim Creator anfordern und an die Marke abrechnen.", "Follow-up purchase recorded. Now request it from the creator and invoice the brand.")).then((r) => r && onClose())}>{t("Nachkauf speichern", "Save follow-up purchase")}</button>
            <button onClick={() => setPruefen(null)}>{t("Zurück", "Back")}</button>
          </div>
        </>
      )}
    </Dialog>
  );
}

/** Die ganze Kooperation stornieren: Grund, Ausfallhonorar in Prozent je offenem Monat, ab welchem Monat. */
export function KooperationStornoDialog({ detail, offene, onClose }: { detail: KoopDetail; offene: readonly MonatInfo[]; onClose: () => void }) {
  const sicht = useBetraegeSichtbar();
  const money = verdeckt(sicht, moneyFmt);
  const prozent = verdeckt(sicht, prozentFmt);
  const { busy, error, run } = useAction();
  const [grund, setGrund] = useState("");
  const [satz, setSatz] = useState("");
  const [ab, setAb] = useState(offene[0]?.month ?? "");
  const [fehler, setFehler] = useState<string | null>(null);
  const [bereit, setBereit] = useState(false);
  const currency = detail.deal.currency;
  const p = prozentAusText(satz);
  const betroffen = offene.filter((m) => !ab || m.month >= ab);
  const summe = betroffen.reduce((a, m) => a + Math.round(((m.period.amount_cents ?? 0) * (p && !Number.isNaN(p) ? p : 0)) / 100), 0);
  const weiter = () => {
    if (grund.trim().length < 3) return setFehler(t("Bitte einen Grund angeben.", "Please state a reason."));
    if (p != null && Number.isNaN(p)) return setFehler(t("Das Ausfallhonorar in Prozent muss zwischen 0 und 100 liegen.", "The cancellation fee in percent must be between 0 and 100."));
    setFehler(null);
    setBereit(true);
  };
  return (
    <Dialog open onClose={onClose} title={t("Kooperation stornieren", "Cancel deal")}>
      {!bereit ? (
        <>
          <p>{t("Offene Monate werden storniert; mit Ausfallhonorar bleibt je Monat der Anteil am vereinbarten Honorar abrechenbar. Gebuchte Monate und Monate mit Rechnungen bleiben unverändert.", "Open months are cancelled; with a cancellation fee, each month's share of the agreed fee stays invoiceable. Booked months and months with invoices stay unchanged.")}</p>
          <label>{t("Grund", "Reason")}<input value={grund} maxLength={500} onChange={(e) => setGrund(e.target.value)} placeholder={t("z. B. Marke stoppt die Kampagne", "e.g. brand stops the campaign")} /></label>
          <div className="grid">
            <label>{t("Ausfallhonorar in Prozent (optional)", "Cancellation fee in percent (optional)")}<input inputMode="decimal" value={satz} onChange={(e) => setSatz(e.target.value)} placeholder={t("leer = ohne Ausfallhonorar", "empty = without cancellation fee")} /></label>
            {offene.length > 1 && (
              <label>{t("Ab Monat", "From month")}<select value={ab} onChange={(e) => setAb(e.target.value)}>{offene.map((m) => <option key={m.period.id} value={m.month}>{monthText(m.month)}</option>)}</select></label>
            )}
          </div>
          {fehler && <p className="tk-error" role="alert">{fehler}</p>}
          <div className="row"><button className="primary" onClick={weiter}>{t("Weiter", "Next")}</button><button onClick={onClose}>{t("Abbrechen", "Cancel")}</button></div>
        </>
      ) : (
        <>
          <p><strong>{t("Bitte prüfen:", "Please check:")}</strong> {betroffen.length === 1 ? t("1 offener Monat", "1 open month") : t(`${betroffen.length} offene Monate`, `${betroffen.length} open months`)} ({betroffen.map((m) => monthText(m.month)).join(", ") || "keiner"}) {p ? t(`mit ${prozent(p)} Ausfallhonorar, zusammen ${money(summe, currency)} netto`, `with ${prozent(p)} cancellation fee, together ${money(summe, currency)} net`) : t("ohne Ausfallhonorar", "without cancellation fee")}{t(". Die Kooperation steht danach auf „Storniert“.", ". The deal is then set to “Cancelled”.")}</p>
          <p>{t("Grund: ", "Reason: ")}{grund.trim()}</p>
          {error && <p className="tk-error" role="alert">{error}</p>}
          <div className="row">
            <button className="danger" disabled={busy !== null} onClick={() => void run("deal-storno", () => geklappt(storniereKooperation(api, detail.deal.id, { grund, prozent: p && !Number.isNaN(p) ? p : null, abMonat: ab || null })),
              t("Kooperation storniert.", "Deal cancelled.")).then((r) => r && onClose())}>{t("Kooperation stornieren", "Cancel deal")}</button>
            <button onClick={() => setBereit(false)}>{t("Zurück", "Back")}</button>
          </div>
        </>
      )}
    </Dialog>
  );
}

/**
 * Bonus nach Kennzahl (z. B. ab 100.000 Aufrufen): vorbereitet mit Prüftermin im Kalender, zählt nicht als Umsatz, bis er als
 * erfüllt markiert ist; dann wird er eine Zusatzposition im gewählten Monat.
 */
export function BonusBereich({ detail, monate }: { detail: KoopDetail; monate: readonly MonatInfo[] }) {
  const sicht = useBetraegeSichtbar();
  const money = verdeckt(sicht, moneyFmt);
  const prozent = verdeckt(sicht, prozentFmt);
  const { busy, error, run } = useAction();
  const [neu, setNeu] = useState(false);
  const [f, setF] = useState({ beschreibung: "Bonus", bedingung: "", betrag: "", pruefen: "", provision: true });
  const [fehler, setFehler] = useState<string | null>(null);
  const [erfuellt, setErfuellt] = useState<{ id: string; monat: string } | null>(null);
  const currency = detail.deal.currency;
  const archiviert = !!detail.deal.archived_at;
  const offen = detail.bonuses.filter((b) => b.status === "pending");
  const entschieden = detail.bonuses.filter((b) => b.status !== "pending");
  if (!detail.bonuses.length && (archiviert || detail.deal.model === "event" || detail.deal.model === "gifting")) return null;
  const anlegen = () => {
    const c = betragAusText(f.betrag);
    if (f.bedingung.trim().length < 3) return setFehler(t("Bitte die Bedingung angeben, z. B. „ab 100.000 Aufrufen“.", "Please state the condition, e.g. “from 100,000 views”."));
    if (c == null || Number.isNaN(c) || c <= 0) return setFehler(t(`Bitte einen Betrag wie 500${decimalSeparator()}00 eingeben.`, `Please enter an amount such as 500${decimalSeparator()}00.`));
    setFehler(null);
    void run("bonus", () => legeBonusAn(api, detail.deal.id, { beschreibung: f.beschreibung, bedingung: f.bedingung, betragCents: c, provisionspflichtig: f.provision, pruefen: f.pruefen || null }),
      f.pruefen ? t("Bonus vorbereitet. Der Prüftermin steht im Kalender.", "Bonus prepared. The review date is in the calendar.") : t("Bonus vorbereitet.", "Bonus prepared.")).then((r) => {
      if (r) {
        setNeu(false);
        setF({ beschreibung: "Bonus", bedingung: "", betrag: "", pruefen: "", provision: true });
      }
    });
  };
  const standard = monate.at(-1)?.month ?? new Date().toISOString().slice(0, 7);
  return (
    <section className="panel stack">
      <h3>{t("Bonus nach Kennzahl", "Bonus by metric")}</h3>
      <p className="source-note">{t("Ein Bonus, der nur bei einer Kennzahl fällig wird, zählt erst als Umsatz, wenn er erfüllt ist.", "A bonus that only becomes due on reaching a metric only counts as revenue once it is achieved.")}</p>
      {offen.map((b) => (
        <div key={b.id} className="task">
          <div>
            <strong>{b.description} · {money(b.amount_cents, currency)}</strong>
            <p>{b.condition}{b.check_on ? t(` · prüfen am ${date(b.check_on)}`, ` · check on ${date(b.check_on)}`) : ""}{b.commissionable ? "" : t(" · provisionsfrei", " · commission-free")}</p>
            {erfuellt?.id === b.id && (
              <div className="row">
                <label>{t("Abrechnen im Monat", "Invoice in month")}<input type="month" value={erfuellt.monat} onChange={(e) => setErfuellt({ id: b.id, monat: e.target.value })} /></label>
                <button className="primary" disabled={busy !== null || !/^\d{4}-\d{2}$/.test(erfuellt.monat)}
                  onClick={() => void run("bonus-ja", () => geklappt(entscheideBonus(api, b.id, true, erfuellt.monat)), t("Bonus als erfüllt gebucht. Er ist jetzt eine Position im Monat.", "Bonus booked as achieved. It is now an item in the month.")).then((r) => r && setErfuellt(null))}>{t("Übernehmen", "Take over")}</button>
                <button onClick={() => setErfuellt(null)}>{t("Abbrechen", "Cancel")}</button>
              </div>
            )}
          </div>
          {!archiviert && erfuellt?.id !== b.id && (
            <div className="row">
              <button onClick={() => setErfuellt({ id: b.id, monat: standard })}>{t("Erfüllt", "Achieved")}</button>
              <button disabled={busy !== null} onClick={() => void run("bonus-nein", () => geklappt(entscheideBonus(api, b.id, false, null)), t("Bonus als nicht erreicht vermerkt.", "Bonus noted as not reached."))}>{t("Nicht erreicht", "Not reached")}</button>
              <button disabled={busy !== null} onClick={() => void run("bonus-weg", () => geklappt(entferneBonus(api, b.id)), t("Bonus entfernt.", "Bonus removed."))} aria-label={t(`Bonus ${b.description} entfernen`, `Remove bonus ${b.description}`)}>{t("Entfernen", "Remove")}</button>
            </div>
          )}
        </div>
      ))}
      {entschieden.map((b) => (
        <div key={b.id} className="task">
          <div><strong>{b.description} · {money(b.amount_cents, currency)}</strong><p>{b.condition}</p></div>
          <span className={`pill ${b.status === "met" ? "success" : ""}`}><span className="pill-zeichen">{b.status === "met" ? "✓" : "○"}</span> {b.status === "met" ? t("Erfüllt", "Achieved") : t("Nicht erreicht", "Not reached")}</span>
        </div>
      ))}
      {!archiviert && !neu && <div className="row"><button onClick={() => setNeu(true)}>{t("Bonus vorbereiten", "Prepare bonus")}</button></div>}
      {neu && (
        <div className="stack">
          <div className="grid">
            <label>{t("Bezeichnung", "Name")}<input value={f.beschreibung} maxLength={200} onChange={(e) => setF({ ...f, beschreibung: e.target.value })} /></label>
            <label>{t("Bedingung", "Condition")}<input value={f.bedingung} maxLength={500} onChange={(e) => setF({ ...f, bedingung: e.target.value })} placeholder={t("z. B. ab 100.000 Aufrufen in 30 Tagen", "e.g. from 100,000 views in 30 days")} /></label>
            <label>{t("Betrag netto in ", "Amount net in ")}{currency}<input inputMode="decimal" value={f.betrag} aria-invalid={betragUnlesbar(f.betrag) || undefined} onChange={(e) => setF({ ...f, betrag: e.target.value })} /><BetragErkannt text={f.betrag} currency={currency} /></label>
            <label>{t("Prüfen am (optional)", "Check on (optional)")}<input type="date" value={f.pruefen} onChange={(e) => setF({ ...f, pruefen: e.target.value })} /></label>
          </div>
          <label className="row tk-koop-wahl"><input type="checkbox" checked={f.provision} onChange={(e) => setF({ ...f, provision: e.target.checked })} /><span>{t("provisionspflichtig", "commissionable")}</span></label>
          {fehler && <p className="tk-error" role="alert">{fehler}</p>}
          <div className="row"><button className="primary" disabled={busy !== null} onClick={anlegen}>{t("Bonus speichern", "Save bonus")}</button><button onClick={() => { setNeu(false); setFehler(null); }}>{t("Abbrechen", "Cancel")}</button></div>
        </div>
      )}
      {error && <p className="tk-error" role="alert">{error}</p>}
    </section>
  );
}
